/**
 * Registration payment amount (#127 slice 2, R2.2, D1/D2/D4).
 *
 * FAMILY uses the household price worked out from the primary's date of
 * birth plus `formData.familyMembers` (D4: the family price follows D1/D2
 * at registration, same as every other point it's charged). Every other
 * membership type is unchanged (sponsor pricing is out of scope, R2/A2 note
 * in requirements.md "Sponsor and individual pricing (unchanged)").
 *
 * Extracted as a pure function so RegistrationWizard's payment step, and
 * ReviewStep/CompEntryStep's displayed totals, can share one calculation
 * that agrees with the server (submit-membership-application resolves the
 * same household price for `amountPaid`).
 */
import { MemberRegistrationForm, MembershipType } from '../types';
import { getMembershipPriceByString } from './membership';
import { familyPriceCents } from '@shared/fees';
import { householdFromMembers } from '@shared/familyLimits';
import { clubYear } from '@shared/membership';

/** The household this registration represents: the primary plus `familyMembers`, for `year` (defaults to the current club year). */
export function registrationHousehold(
  formData: Pick<MemberRegistrationForm, 'dateOfBirth' | 'familyMembers'>,
  year: number = clubYear()
) {
  return householdFromMembers(
    [
      { dateOfBirth: formData.dateOfBirth, isPrimary: true },
      ...(formData.familyMembers || []).map((m) => ({ dateOfBirth: m.dateOfBirth })),
    ],
    year
  );
}

/**
 * The amount payable in cents for this registration. FAMILY is the
 * household price; every other type is the flat per-type fee (unchanged).
 */
export function registrationAmountCents(
  formData: Pick<MemberRegistrationForm, 'membershipType' | 'dateOfBirth' | 'familyMembers'>
): number {
  if (formData.membershipType === MembershipType.FAMILY) {
    return familyPriceCents(registrationHousehold(formData));
  }
  return Math.round(getMembershipPriceByString(formData.membershipType) * 100);
}

/** The amount payable in whole/decimal dollars, for display. */
export function registrationAmountDollars(
  formData: Pick<MemberRegistrationForm, 'membershipType' | 'dateOfBirth' | 'familyMembers'>
): number {
  return registrationAmountCents(formData) / 100;
}
